import type {
  AppNotification,
  CashoutRequestInput,
  CashoutRequestResponse,
  DeactivateAccountResponse,
  EarningsData,
  EarningsFilters,
  MemberCheckIn,
  Panelist,
  PointsBalance,
  PreTaskResponse,
  Profile,
  ProfileFieldsData,
  PublicPanel,
  ResendSignupVerificationResponse,
  SessionContext,
  SessionValidity,
  SignOutResponse,
  SignupInput,
  SignupResponse,
  Task,
  UpdateMemberCheckInInput,
  UpdateProfileInput,
} from "../types";
import { apiBlobRequest, apiRequest } from "./client";

const getFingerprint = (): UserClientFingerprint => ({
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  languageCode: navigator.language,
  screenHeight: window.screen.height,
  screenWidth: window.screen.width,
});
export function fetchPanelist() {
  return apiRequest<Panelist>("/panelist");
}

export function fetchBalance() {
  return apiRequest<PointsBalance>("/balance");
}

export const getLaunchToken = () => apiRequest<string>("/launch-token");
export function fetchPublicPanel(panelId?: number) {
  return apiRequest<PublicPanel>(
    typeof panelId === "number" && Number.isFinite(panelId)
      ? `/public/panel/${panelId}`
      : "/public/panel",
  );
}

export function fetchSessionContext(panelId?: number) {
  const params = new URLSearchParams();
  if (panelId) params.set("panelId", panelId.toString());
  return apiRequest<SessionContext>(`/session/context`, {
    method: "POST",
    body: { fingerprint: getFingerprint(), panelId },
  });
}

export function probeSessionValidity() {
  return apiRequest<SessionValidity>("/session/valid");
}

export function updateSessionContext(panelId: number) {
  return apiRequest<SessionContext>("/session/context", {
    body: { panelId },
    method: "PUT",
  });
}

export function fetchTasks(
  rejectedTaskIds: string[] = [],
  forceRefresh = false,
) {
  return apiRequest<Task[]>("/tasks", {
    params: {
      excludeTaskId: rejectedTaskIds?.join(","),
      fingerprint: getFingerprint(),
      forceRefresh,
    },
  });
}
export function fetchTask(taskId: string) {
  return apiRequest<Task>(`/tasks/${taskId}`);
}
export type TaskEvent = "selected" | "skipped" | "seen";
export function sendTaskEvent(taskId: string, event: TaskEvent) {
  return apiRequest<void>(`/tasks/${taskId}/events`, {
    body: { event },
    method: "POST",
  });
}

export function fetchFile(fileId: string) {
  return apiBlobRequest(`/files/${encodeURIComponent(fileId)}`);
}

export function fetchPreTask(taskId: string) {
  return apiRequest<PreTaskResponse>(`/tasks/${taskId}/pre`);
}
export function updateTaskFingerprint(completionToken: string) {
  return apiRequest<{ success: boolean; error?: string }>(
    `/tasks/${completionToken}/fingerprint`,
    {
      method: "POST",
      body: { fingerprint: getFingerprint() },
    },
  );
}

export function fetchEarningsHistory(filters: EarningsFilters) {
  const params = new URLSearchParams({
    date_range: filters.dateRange,
    status: filters.status,
  });
  return apiRequest<EarningsData>(`/earnings?${params.toString()}`);
}

export function fetchProfile() {
  return apiRequest<Profile>("/profile");
}

export function fetchProfileFields() {
  return apiRequest<ProfileFieldsData>("/profile/fields");
}

export function fetchPanelFields(panelId?: number) {
  return apiRequest<ProfileFieldsData>(
    typeof panelId === "number" && Number.isFinite(panelId)
      ? `/public/panel/${panelId}/fields`
      : "/public/panel/fields",
  );
}

export function fetchRecentNotifications() {
  return apiRequest<AppNotification[]>("/notifications/recent");
}
export function fetchNotification(id: string) {
  return apiRequest<AppNotification>(`/notifications/${id}`);
}
export function fetchFlexNotificationUrl(domain: string, itemId: number) {
  return apiRequest<string>(
    `/notifications/flex?domain=${domain}&itemId=${itemId}`,
  );
}
export function markNotificationRead(ids: string[]) {
  return apiRequest<void>(`/notifications/read`, {
    body: { ids },
    method: "POST",
  });
}

export type UserClientFingerprint = {
  timezone: string;
  languageCode: string;
  screenHeight: number;
  screenWidth: number;
};
export function requestCashout(input: CashoutRequestInput) {
  return apiRequest<CashoutRequestResponse>("/cashout/request", {
    body: input,
    method: "POST",
  });
}

export function updateProfile(input: Partial<UpdateProfileInput>) {
  return apiRequest<Profile>("/profile", {
    body: input,
    method: "PUT",
  });
}

export function updateMemberCheckIn(
  checkInId: number,
  input: UpdateMemberCheckInInput,
) {
  return apiRequest<MemberCheckIn>(`/checkins/${checkInId}`, {
    body: input,
    method: "PATCH",
  });
}

export function signUpPanelist(
  panelId: number | undefined,
  input: SignupInput,
) {
  const fingerprint = getFingerprint();
  const _panelId =
    typeof panelId === "number" && Number.isFinite(panelId)
      ? panelId
      : undefined;
  const path = _panelId ? `/signup/${_panelId}` : "/signup";
  return apiRequest<SignupResponse>(path, {
    body: { ...input, panelId: _panelId, fingerprint },
    method: "POST",
  });
}

export function resendSignupVerification(email: string) {
  return apiRequest<ResendSignupVerificationResponse>("/auth/verify/resend", {
    body: { email },
    method: "POST",
  });
}

export function signOut() {
  return apiRequest<SignOutResponse>("/auth/logout", {
    method: "POST",
  });
}
export type DeactivateAccountOptions = {
  removeLogin?: boolean;
  removeAllAccounts?: boolean;
};
export function deactivateAccount(options?: DeactivateAccountOptions) {
  return apiRequest<DeactivateAccountResponse>("/panelist/delete-account", {
    method: "POST",
    body: options,
  });
}
